import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection';
import OrderChannelResolver, { KeyAccountOrderChannels, KeyAccountsLanguageOrderChannels } from './order-channel-resolver';
import { ORDER_CHANNEL_KEY, OrderChannels } from '../types/orderChannels';

interface KeyAccount {
  tpAccountId: string
  tpImage: string
  tpImageAlt: string
  tpName: string
}

interface TradingPartner extends Omit<KeyAccount, 'tpAccountId'> {
  tpAccountId: number
  orderChannels: OrderChannels
}

class TradingPartnerResolver {
  private orderChannelResolver: OrderChannelResolver;
  private language: string;
  private keyAccounts: Array<KeyAccount>;
  private blockedOrderChannels: Array<ORDER_CHANNEL_KEY>
  private orderChannels: KeyAccountsLanguageOrderChannels;
  private browserDetection : UtilBrowserDetection;

  constructor(orderChannelResolver: OrderChannelResolver, keyAccounts: Array<KeyAccount>, blockedOrderChannels: Array<ORDER_CHANNEL_KEY>, language: string) {
    this.orderChannelResolver = orderChannelResolver;
    this.language = language;
    this.keyAccounts = keyAccounts;
    this.blockedOrderChannels = blockedOrderChannels;

    this.browserDetection = new UtilBrowserDetection();
  }

  private async getOrderChannels(): Promise<KeyAccountsLanguageOrderChannels> {
    if (this.orderChannels) {
      return this.orderChannels;
    }

    return await this.orderChannelResolver.resolve()
  }

  async resolve(): Promise<TradingPartner[]> {
    const operatingSystem = this.browserDetection.getOs();

    // get all partner channels with matching language and device
    const orderChannels = await this.getOrderChannels();
    const filteredOrderChannels = Object.keys(orderChannels).reduce((accumulator, keyAccount) => {
      const languageChannel = orderChannels[keyAccount][this.language];
      if (! languageChannel) {
        return accumulator;
      }

      const channels = this.getUserDeviceMatchingOrderChannels(languageChannel, operatingSystem);

      // remove partners with no available channels
      if (! Object.keys(channels).length) {
        return accumulator;
      }

      accumulator[keyAccount] = channels;
      return accumulator;
    }, {});

    return this.keyAccounts.filter(keyAccount => filteredOrderChannels[keyAccount.tpAccountId]).map(keyAccount => {
      return {
        ...keyAccount,
        tpAccountId: parseInt(keyAccount.tpAccountId),
        orderChannels: filteredOrderChannels[keyAccount.tpAccountId]
      };
    });
  }

  private fixOperatingSystemNameForJson(operatingSystem: string) : string{
    return operatingSystem === 'macos' ? 'mac' : operatingSystem;
  }

  private getUserDeviceMatchingOrderChannels(languageChannel: KeyAccountOrderChannels, operatingSystem: string): OrderChannels {
    const channels : OrderChannels = {};
    const fixedOperatingSystemNameForJson = this.fixOperatingSystemNameForJson(operatingSystem)

    // always add web editor excpet its blocked
    if (! this.blockedOrderChannels.includes(ORDER_CHANNEL_KEY.ONLINE_EDITOR) && languageChannel.webOrder?.editor) {
      channels.online = languageChannel.webOrder.editor;
    }

    // resolve hps or mps order channel
    if (! this.blockedOrderChannels.includes(ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE) && languageChannel.desktopOrder && Object.keys(languageChannel.desktopOrder).includes(fixedOperatingSystemNameForJson)) {
      channels.software = {
        os: operatingSystem,
        type: ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE,
        link: languageChannel.desktopOrder[fixedOperatingSystemNameForJson]
      };
    } else if (! this.blockedOrderChannels.includes(ORDER_CHANNEL_KEY.MOBILE_SOFTWARE) && languageChannel.mobileOrder && Object.keys(languageChannel.mobileOrder).includes(fixedOperatingSystemNameForJson)) {
      channels.software = {
        os: operatingSystem,
        type: ORDER_CHANNEL_KEY.MOBILE_SOFTWARE,
        link: languageChannel.mobileOrder[fixedOperatingSystemNameForJson]
      };
    }

    return channels;
  }

  async getKeyAccountOrderChannels(keyAccountId: number): Promise<OrderChannels|null> {
    const orderChannels = await this.getOrderChannels();
    const keyAccountOrderChannel = orderChannels[keyAccountId]
    if (! keyAccountOrderChannel) {
      return null;
    }

    const languageChannel = keyAccountOrderChannel[this.language]
    if (! languageChannel) {
      return null;
    }

    const operatingSystem = this.browserDetection.getOs();

    const channels = this.getUserDeviceMatchingOrderChannels(languageChannel, operatingSystem);

    // remove partners with no available channels
    if (! Object.keys(channels).length) {
      return null;
    }

    return channels;
  }
}

export default TradingPartnerResolver;
export type { TradingPartner, OrderChannels };
